<template>
  <div id="app">
    <api-sports-widget
      v-if="apiFootballKey"
      data-type="config"
      data-sport="football"
      data-lang="en"
      data-theme="dark"
      data-show-errors="true"
      data-show-logos="true"
      :data-key="apiFootballKey"
    ></api-sports-widget>
    <nav v-if="showNavbar" class="navbar" :class="{ scrolled: isScrolled }">
      <div class="nav-container">
        <div class="nav-left">
          <h1>UEFA POINTS MANAGEMENT</h1>
        </div>
        <div class="nav-links">
          <router-link to="/">Home</router-link>
          <router-link to="/fixtures">Calendar</router-link>
          <router-link to="/uefa-points">UEFA Points</router-link>
          <router-link to="/points-ev">Points Ev</router-link>
          <router-link to="/uefa-associations">UEFA Associations</router-link>
          <router-link to="/competitions">Competitions</router-link>
          <router-link v-if="isAdmin" to="/uefa-club-names">Master Data</router-link>
          <router-link v-if="isAdmin" to="/season-config">Season Config</router-link>
          <router-link v-if="isAdmin" to="/season-history">History</router-link>
          <router-link v-if="isAdmin" to="/api-data-management">API Data Mgmt</router-link>
          <router-link v-if="manageUsers" to="/users">Users</router-link>
          <router-link to="/kassiesa-monitor">Kassiesa Monitor</router-link>
          <router-link to="/teams">Match Day</router-link>
          <button
            class="nav-action-icon"
            title="Open Match Day in new window"
            aria-label="Open Match Day in new window"
            @click="openMatchDayWindow"
          >
            ↗
          </button>
          <router-link to="/tables">Standings</router-link>
          <button
            class="nav-action-icon"
            title="Open Standings in new window"
            aria-label="Open Standings in new window"
            @click="openStandingsWindow"
          >
            ↗
          </button>
          <button
            class="nav-ai-btn"
            title="Open AI Assistant"
            aria-label="Open AI Assistant"
            @click="showAssistant = true"
          >
            AI Assistant
          </button>
        </div>
      </div>
    </nav>
    
    <main class="main-content" :class="{ popout: isStandalonePopout }">
      <router-view v-slot="{ Component, route }">
        <keep-alive>
          <component
            v-if="route.meta?.keepAlive"
            :is="Component"
            :key="route.fullPath"
          />
        </keep-alive>
        <component
          v-if="!route.meta?.keepAlive"
          :is="Component"
          :key="route.fullPath"
        />
      </router-view>
    </main>
    <AIAssistantDrawer
      :open="showAssistant"
      :initial-season="assistantSeasonDefault"
      :request="assistantRequest"
      @close="showAssistant = false"
    />
  </div>
</template>

<script>
import AIAssistantDrawer from '@/components/AIAssistantDrawer.vue'
import { access, hasRole } from '@/services/access'

export default {
  name: 'App',
  components: {
    AIAssistantDrawer
  },
  data() {
    return {
      isScrolled: false,
      showAssistant: false,
      assistantRequest: null
    }
  },
  mounted() {
    this.handleScroll()
    window.addEventListener('scroll', this.handleScroll, { passive: true })
    window.addEventListener('open-ai-assistant', this.handleOpenAssistantEvent)
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
    window.removeEventListener('open-ai-assistant', this.handleOpenAssistantEvent)
  },
  computed: {
    isAdmin() { return hasRole('admin') },
    manageUsers() { return this.isAdmin && access.value?.mode === 'private' },
    apiFootballKey() {
      return process.env.VUE_APP_API_FOOTBALL_KEY || ''
    },
    isMatchDayPopout() {
      return this.$route?.name === 'teams' && String(this.$route?.query?.popout || '') === '1'
    },
    isStandingsPopout() {
      return this.$route?.name === 'tables' && String(this.$route?.query?.popout || '') === '1'
    },
    isStandalonePopout() {
      return this.isMatchDayPopout || this.isStandingsPopout
    },
    showNavbar() {
      return !this.isStandalonePopout
    },
    assistantSeasonDefault() {
      return '2026/2027'
    }
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 10
    },
    openMatchDayWindow() {
      const target = this.$router.resolve({
        name: 'teams',
        query: { popout: '1' }
      })
      const popup = window.open(
        target.href,
        'uefa-matchday-popout',
        'width=1680,height=960,menubar=no,toolbar=no,location=yes,status=no,resizable=yes,scrollbars=yes'
      )
      if (!popup) {
        window.location.href = target.href
      }
    },
    openStandingsWindow() {
      const target = this.$router.resolve({
        name: 'tables',
        query: { popout: '1' }
      })
      const popup = window.open(
        target.href,
        'uefa-standings-popout',
        'width=1680,height=960,menubar=no,toolbar=no,location=yes,status=no,resizable=yes,scrollbars=yes'
      )
      if (!popup) {
        window.location.href = target.href
      }
    },
    handleOpenAssistantEvent(event) {
      const detail = event?.detail && typeof event.detail === 'object' ? event.detail : {}
      this.assistantRequest = {
        ...detail,
        _ts: Date.now()
      }
      this.showAssistant = true
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Barlow', 'Segoe UI', Tahoma, sans-serif;
  background: var(--uefa-bg);
  color: var(--uefa-text);
}

#app {
  min-height: 100vh;
}

.navbar {
  position: sticky;
  top: 0;
  z-index: 120;
  background: linear-gradient(110deg, var(--uefa-nav) 0%, var(--uefa-nav-2) 65%, #12438e 100%);
  color: white;
  padding: 0.85rem 0;
  box-shadow: 0 10px 30px rgba(9, 26, 59, 0.35);
  border-bottom: 2px solid rgba(25, 181, 255, 0.5);
  backdrop-filter: saturate(1.1) blur(2px);
  transition: box-shadow 0.22s ease, border-bottom-color 0.22s ease, background 0.22s ease;
}

.navbar.scrolled {
  box-shadow: 0 16px 34px rgba(9, 26, 59, 0.46);
  border-bottom-color: rgba(25, 181, 255, 0.75);
  background: linear-gradient(110deg, #071631 0%, #0d2858 62%, #103a7a 100%);
}

.nav-container {
  max-width: none;
  width: 100%;
  margin: 0;
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 1.5rem;
  padding: 0 clamp(12px, 2.2vw, 34px);
}

.nav-container h1 {
  font-size: 0.98rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  white-space: nowrap;
  color: #ffffff;
}

.nav-links {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.2rem;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  padding-bottom: 2px;
  scrollbar-width: thin;
  scrollbar-color: rgba(233, 242, 255, 0.35) transparent;
}

.nav-links::-webkit-scrollbar {
  height: 6px;
}

.nav-links::-webkit-scrollbar-thumb {
  background: rgba(233, 242, 255, 0.35);
  border-radius: 999px;
}

.nav-links a {
  color: #e9f2ff;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.92rem;
  padding: 0.4rem 0.66rem;
  border-radius: 999px;
  border: 1px solid transparent;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.nav-links a:hover, .nav-links a.router-link-active {
  color: #ffffff;
  border-color: rgba(153, 222, 255, 0.45);
  background: rgba(6, 20, 44, 0.55);
}

.nav-action-icon {
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 999px;
  border: 1px solid transparent;
  background: transparent;
  color: #e9f2ff;
  font-size: 0.92rem;
  line-height: 1;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.nav-action-icon:hover {
  color: #ffffff;
  border-color: rgba(153, 222, 255, 0.45);
  background: rgba(6, 20, 44, 0.55);
}

.nav-ai-btn {
  border: 1px solid rgba(153, 222, 255, 0.45);
  background: rgba(6, 20, 44, 0.55);
  color: #ffffff;
  border-radius: 999px;
  font-size: 0.88rem;
  font-weight: 700;
  padding: 0.38rem 0.76rem;
  white-space: nowrap;
  cursor: pointer;
}

.nav-ai-btn:hover {
  background: rgba(14, 45, 93, 0.72);
}

.main-content {
  max-width: none;
  width: 100%;
  margin: 0;
  padding: 1.25rem clamp(12px, 2.2vw, 34px) 2rem;
}

.main-content.popout {
  padding: 0.85rem 1rem 1rem;
}

@media (max-width: 1100px) {
  .nav-container {
    gap: 0.9rem;
  }

  .nav-links a {
    font-size: 0.86rem;
    padding: 0.38rem 0.65rem;
  }

  .nav-action-icon {
    width: 26px;
    height: 26px;
    min-width: 26px;
    font-size: 0.84rem;
  }
}

@media (max-width: 860px) {
  .nav-container {
    grid-template-columns: 1fr;
    gap: 0.6rem;
  }

  .nav-left {
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }
}

</style>
